'use client';

import { useState, useEffect } from 'react';

import ProductHighlight from './ProductHighlight';
import CertificationBadge from './CertificationBadge';
import InfoBox from './InfoBox';
import { useLanguage } from '@/contexts/LanguageContext';


interface ProductCategory {
  title: string;
  description: string;
  priceFrom: string;
  features: string[];
  ctaText: string;
  ctaHref: string;
  variant: 'subscription' | 'onetime';
}

interface MetalHighlight {
  metal: string;
  weights: string[];
  purity: string;
  image: string;
  imageAlt: string;
}

interface Certification {
  icon: string;
  title: string;
  description: string;
}

const ProductsInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const metalHighlights: MetalHighlight[] = [
  {
    metal: t('products.metalHighlights.gold.metal'),
    weights: ["0,5g", "1g", "2g", "2,5g", "5g", "10g", "20g", "31,1g (1 oz)", "50g"],
    purity: t('products.metalHighlights.gold.purity'),
    image: "/assets/images/18-1769616042208.jpg",
    imageAlt: t('products.metalHighlights.gold.imageAlt')
  },
  {
    metal: t('products.metalHighlights.silver.metal'),
    weights: ["10g", "20g", "31,1g (1 oz)", "50g", "100g", "250g", "500g", "1000g (1 kg)"],
    purity: t('products.metalHighlights.silver.purity'),
    image: "/assets/images/11-1769616284609.jpg",
    imageAlt: t('products.metalHighlights.silver.imageAlt')
  }];


  const certifications: Certification[] = [
  {
    icon: "ShieldCheckIcon",
    title: t('products.certifications.madeInGermany.title'),
    description: t('products.certifications.madeInGermany.description')
  },
  {
    icon: "CheckBadgeIcon",
    title: t('products.certifications.lbmaCertified.title'),
    description: t('products.certifications.lbmaCertified.description')
  },
  {
    icon: "DocumentCheckIcon",
    title: t('products.certifications.authenticityCertificate.title'),
    description: t('products.certifications.authenticityCertificate.description')
  }];


  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="mx-auto max-w-content px-6 lg:px-8 py-16 lg:py-20">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-muted rounded-md w-1/3"></div>
            <div className="h-6 bg-muted rounded-md w-2/3"></div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="h-96 bg-muted rounded-lg"></div>
              <div className="h-96 bg-muted rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-secondary relative overflow-hidden">
      <div className="mx-auto max-w-content px-6 lg:px-8 py-16 lg:py-20">
        {/* Header Section */}
        <div className="mb-16 lg:mb-20 text-center">
          <h1 className="font-heading text-5xl lg:text-6xl font-bold text-foreground mb-6 tracking-tight">
            {t('products.pageTitle')}
          </h1>
        </div>

        {/* Product Highlights Section */}
        <div className="mb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {metalHighlights.map((highlight, index) =>
            <ProductHighlight key={index} {...highlight} />
            )}
          </div>
        </div>

        {/* Certifications Section */}
        <div className="mb-20">
          <h2 className="font-heading text-4xl font-bold text-foreground mb-10 text-center tracking-tight">
            {t('products.certificationSection.title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {certifications.map((cert, index) =>
            <CertificationBadge key={index} {...cert} />
            )}
          </div>
        </div>

        {/* Buyback Guarantee */}
        <InfoBox
          title={t('products.infoBox.title')}
          description={t('products.infoBox.description')}
          icon="ShieldCheckIcon" />

      </div>
    </div>);

};

export default ProductsInteractive;